import Phaser from 'phaser';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { faceMove, faceIdle, type Facing } from '../world/playerFacing';
import { trailSign } from '../world/trailSign';
import { TX, AUDIO } from '../registry/keys';
import { CONFIG } from '../data/config';
import { playSfx } from '../audio/sfx';
import { store } from '../state/store';
import { getItem } from '../data/items';
import { personalization } from '../data/personalization';
import { tileKey, type TileXY } from '../types';
import { getHat, hatTexture } from '../data/hats';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const FW = 12;
const FH = 9;
const EXIT: TileXY = { x: 6, y: 8 }; // trail home (gap in the bottom treeline)
const BEACH: TileXY = { x: 6, y: 0 }; // trail to the beach (gap in the top treeline)
const FORT: TileXY = { x: 10, y: 2 }; // Clara's secret tree fort
const TOWN: TileXY = { x: 0, y: 4 }; // trail to town (gap in the left treeline)
const ENTRY: TileXY = { x: 6, y: 7 };
const ORIGIN_Y = 0.7;

interface Spot {
  x: number;
  y: number;
  item: string;
  tex: string;
}
/** Forage spots (index = forage flag). Refresh each morning (flags cleared on rollover). */
const FORAGE: Spot[] = [
  { x: 2, y: 2, item: 'berry', tex: TX.berryBush },
  { x: 9, y: 2, item: 'berry', tex: TX.berryBush },
  { x: 3, y: 5, item: 'mushroom', tex: TX.forageMushroom },
  { x: 8, y: 5, item: 'mushroom', tex: TX.forageMushroom },
  { x: 5, y: 3, item: 'herb', tex: TX.herbPlant },
  { x: 6, y: 5, item: 'wood', tex: TX.stump },
];
/** Scattered interior trees (decorative + blocking) — clear of forage/entry/exit. */
const TREES: TileXY[] = [
  { x: 2, y: 4 }, { x: 9, y: 4 }, { x: 4, y: 6 }, { x: 8, y: 6 }, { x: 5, y: 6 },
];

/** The forest: tap-to-move, forage from spots, tap the trail to return home. */
export class ForestScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private blocked = new Set<string>();
  private path: TileXY[] = [];
  private pendingArrival: (() => void) | null = null;
  private playerTile: TileXY = { ...ENTRY };
  private leaving = false;
  private lastFacing: Facing = 'down';
  private hat?: Phaser.GameObjects.Image;
  private spotSprites = new Map<number, Phaser.GameObjects.Image>();
  private treeSprites = new Map<number, Phaser.GameObjects.Image>();
  private forestPet?: Phaser.GameObjects.Image;
  private claraFort?: Phaser.GameObjects.Image;

  constructor() {
    super('Forest');
  }

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }

  create(): void {
    this.leaving = false;
    this.blocked.clear();
    this.path = [];
    this.spotSprites.clear();
    this.cameras.main.setBackgroundColor('#1c2a18');
    const short = Math.min(this.scale.width, this.scale.height);
    this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(short / (FW * TS)), CONFIG.minZoom, CONFIG.maxZoom));
    this.cameras.main.centerOn((FW * TS) / 2, (FH * TS) / 2);

    this.buildForestMap();

    this.player = this.add.sprite(ENTRY.x * TS + TS / 2, ENTRY.y * TS + TS, TX.player, 0).setOrigin(0.5, ORIGIN_Y);
    this.player.setDepth(ENTRY.y * TS + TS);
    this.playerTile = { ...ENTRY };
    this.applyHat();

    this.add
      .text(this.scale.width / 2, 8, 'Forest — forage, then tap the trail to head home', {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#cfe0c4',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setResolution(2)
      .setDepth(1000);

    this.input.on('pointerdown', this.onTap, this);
    this.time.addEvent({ delay: 8000, loop: true, callback: () => { if (Math.random() < 0.55) this.spawnForestBird(); } });
    this.time.addEvent({ delay: 5000, loop: true, callback: () => { if (Math.random() < 0.3) playSfx(this, AUDIO.chirp); } }); // distant birdsong
    this.maybeSpawnCompanions();
  }

  /** Sometimes the pet roams the forest, and sometimes Clara is up in her tree fort singing. */
  private maybeSpawnCompanions(): void {
    if (Math.random() < 0.55) {
      const spot = this.randomWalkable();
      if (spot) {
        const tex = store.get().petType === 'dog' ? TX.petDog : TX.petCat;
        this.forestPet = this.add.image(spot.x * TS + TS / 2, spot.y * TS + TS, tex).setOrigin(0.5, 1).setDepth(spot.y * TS + TS);
        this.time.addEvent({ delay: 2600, loop: true, callback: () => this.wanderForestPet() });
        this.time.delayedCall(500, () => this.ui.showToast(`Your ${store.get().petType} wandered into the forest! 🐾`, 'info'));
      }
    }
    if (Math.random() < 0.45) {
      this.claraFort = this.add
        .image(FORT.x * TS + TS / 2, FORT.y * TS + TS + 3, TX.clara)
        .setOrigin(0.5, 1)
        .setDepth(FORT.y * TS + TS + 1)
        .setScale(1.1);
      this.time.addEvent({ delay: 3000, loop: true, callback: () => this.claraSings() });
      this.time.delayedCall(900, () => this.ui.showToast(`You can hear ${personalization.clara.name} singing 🎵`, 'good'));
    }
  }

  private randomWalkable(): TileXY | null {
    for (let i = 0; i < 24; i++) {
      const x = Phaser.Math.Between(2, FW - 3);
      const y = Phaser.Math.Between(2, FH - 3);
      if (!this.isBlocked(x, y)) return { x, y };
    }
    return null;
  }

  private wanderForestPet(): void {
    if (!this.forestPet) return;
    const bx = Math.floor(this.forestPet.x / TS);
    const by = Math.floor((this.forestPet.y - 1) / TS);
    for (let i = 0; i < 6; i++) {
      const nx = Phaser.Math.Clamp(bx + Phaser.Math.Between(-2, 2), 1, FW - 2);
      const ny = Phaser.Math.Clamp(by + Phaser.Math.Between(-2, 2), 1, FH - 2);
      if (!this.isBlocked(nx, ny)) {
        this.forestPet.setFlipX(nx * TS + TS / 2 < this.forestPet.x);
        this.tweens.add({
          targets: this.forestPet,
          x: nx * TS + TS / 2,
          y: ny * TS + TS,
          duration: 900,
          ease: 'Sine.easeInOut',
          onUpdate: () => this.forestPet?.setDepth(this.forestPet.y),
        });
        if (Math.random() < 0.3) playSfx(this, store.get().petType === 'dog' ? AUDIO.bark : AUDIO.meow);
        break;
      }
    }
  }

  private claraSings(): void {
    if (!this.claraFort) return;
    const note = this.add
      .text(this.claraFort.x + Phaser.Math.Between(-6, 6), this.claraFort.y - 20, ['🎵', '🎶', '♪'][Math.floor(Math.random() * 3)]!, { fontFamily: 'monospace', fontSize: '12px' })
      .setOrigin(0.5)
      .setDepth(9000)
      .setResolution(2);
    this.tweens.add({ targets: note, y: note.y - 22, alpha: 0, duration: 1900, ease: 'Sine.easeOut', onComplete: () => note.destroy() });
    if (Math.random() < 0.5) playSfx(this, AUDIO.baby);
  }

  /** A little songbird (robin / bluebird / finch) flits across the canopy. */
  private spawnForestBird(): void {
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y = Phaser.Math.Between(8, Math.floor(FH * TS * 0.5));
    const tint = [0xe07a4a, 0x5a8ad0, 0xf2c14e][Math.floor(Math.random() * 3)]!;
    const bird = this.add
      .image(dir > 0 ? -16 : FW * TS + 16, y, TX.bird)
      .setDepth(8000)
      .setScale(1.1)
      .setTint(tint)
      .setFlipX(dir < 0);
    playSfx(this, AUDIO.chirp);
    this.tweens.add({
      targets: bird,
      x: dir > 0 ? FW * TS + 16 : -16,
      y: y + Phaser.Math.Between(-20, 20),
      duration: Phaser.Math.Between(4500, 7000),
      onComplete: () => bird.destroy(),
    });
  }

  private addTree(x: number, y: number): void {
    this.add.image(x * TS + TS / 2, y * TS + TS, TX.forestTree).setOrigin(0.5, 1).setDepth(y * TS + TS);
    this.blocked.add(tileKey(x, y));
  }

  private buildForestMap(): void {
    for (let y = 0; y < FH; y++) {
      for (let x = 0; x < FW; x++) {
        this.add.image(x * TS, y * TS, TX.forestGround).setOrigin(0, 0).setDepth(0);
      }
    }
    // border treeline (except the trail gap)
    for (let y = 0; y < FH; y++) {
      for (let x = 0; x < FW; x++) {
        const border = x === 0 || x === FW - 1 || y === 0 || y === FH - 1;
        const gap = (x === EXIT.x && y === EXIT.y) || (x === BEACH.x && y === BEACH.y) || (x === TOWN.x && y === TOWN.y);
        if (border && !gap) this.addTree(x, y);
      }
    }
    this.add.image(EXIT.x * TS, EXIT.y * TS, TX.trailArch).setOrigin(0, 0).setDepth(1); // trail home
    this.add.image(BEACH.x * TS, BEACH.y * TS, TX.trailArch).setOrigin(0, 0).setDepth(1); // trail to the beach
    this.add.image(TOWN.x * TS, TOWN.y * TS, TX.trailArch).setOrigin(0, 0).setDepth(1); // trail to town
    trailSign(this, EXIT.x, EXIT.y - 1, '🏡 Farm');
    trailSign(this, BEACH.x, BEACH.y + 1, '🏖 Beach');
    trailSign(this, TOWN.x + 1, TOWN.y, '🏘 Town');
    this.add.image(FORT.x * TS + TS / 2, FORT.y * TS + TS, TX.treefort).setOrigin(0.5, 1).setDepth(FORT.y * TS + TS);
    this.blocked.add(tileKey(FORT.x, FORT.y));
    TREES.forEach((t, i) => {
      const chopped = !!store.get().flags['foragetree' + i];
      const img = this.add
        .image(t.x * TS + TS / 2, t.y * TS + TS, TX.forestTree)
        .setOrigin(0.5, 1)
        .setDepth(t.y * TS + TS)
        .setAlpha(chopped ? 0.25 : 1);
      this.treeSprites.set(i, img);
      if (!chopped) this.blocked.add(tileKey(t.x, t.y));
    });

    FORAGE.forEach((s, i) => {
      const taken = !!store.get().flags['forage' + i];
      const img = this.add
        .image(s.x * TS + TS / 2, s.y * TS + TS, s.tex)
        .setOrigin(0.5, 1)
        .setDepth(s.y * TS + TS)
        .setAlpha(taken ? 0.3 : 1);
      this.spotSprites.set(i, img);
      this.blocked.add(tileKey(s.x, s.y));
    });
  }

  private isBlocked = (x: number, y: number): boolean =>
    x < 0 || y < 0 || x >= FW || y >= FH || this.blocked.has(tileKey(x, y));

  private onTap(pointer: Phaser.Input.Pointer): void {
    if (this.leaving || this.ui.isModalOpen() || this.ui.consumesPointer(pointer.x, pointer.y)) return;
    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= FW || tile.y >= FH) return;

    const idx = FORAGE.findIndex((s) => s.x === tile.x && s.y === tile.y);
    if (idx >= 0) return this.routeToInteractable(tile, () => this.forage(idx));
    const ti = TREES.findIndex((t) => t.x === tile.x && t.y === tile.y);
    if (ti >= 0 && !store.get().flags['foragetree' + ti]) return this.routeToInteractable(tile, () => this.chopForestTree(ti));
    if (tile.x === EXIT.x && tile.y === EXIT.y) return this.routeTo(tile, () => this.leave());
    if (tile.x === BEACH.x && tile.y === BEACH.y) return this.routeTo(tile, () => this.enterBeach());
    if (tile.x === TOWN.x && tile.y === TOWN.y) return this.routeTo(tile, () => this.enterTown());
    if (tile.x === FORT.x && tile.y === FORT.y) return this.routeToInteractable(tile, () => this.fortTreasure());
    if (!this.isBlocked(tile.x, tile.y)) this.routeTo(tile, null);
  }

  /** Chop a forest tree for wood — regrows next morning (the forage flags clear daily). */
  private actionBob(): void {
    this.player.setScale(1, 1);
    this.tweens.add({ targets: this.player, scaleX: 1.12, scaleY: 0.85, duration: 95, yoyo: true, ease: 'Quad.easeOut', onComplete: () => this.player.setScale(1, 1) });
  }

  /** Swing the axe (real action frames), then restore the walk sheet. */
  private playerSwing(): void {
    if (!this.anims.exists('act-swing')) return;
    this.player.setTexture(TX.playerAction);
    this.player.play('act-swing');
    this.player.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.player.setTexture(TX.player);
      this.player.setFrame(0);
    });
  }

  private chopForestTree(idx: number): void {
    if (store.get().flags['foragetree' + idx]) return;
    if (!store.trySpendEnergy('chop')) {
      this.ui.showToast('Too tired to chop', 'warn');
      return;
    }
    this.actionBob();
    const t = TREES[idx]!;
    store.update((s) => (s.flags['foragetree' + idx] = true));
    store.addItem('wood', Phaser.Math.Between(1, 2));
    this.treeSprites.get(idx)?.setAlpha(0.25);
    this.blocked.delete(tileKey(t.x, t.y));
    playSfx(this, AUDIO.chop);
    this.playerSwing();
    this.ui.showToast('+ Wood 🪵 — regrows tomorrow', 'good');
  }

  private enterBeach(): void {
    this.scene.launch('Beach');
    this.scene.pause();
  }

  private enterTown(): void {
    this.scene.launch('Town');
    this.scene.pause();
  }

  /** Clara's secret tree fort — a one-time hidden treasure. */
  private fortTreasure(): void {
    if (store.get().flags['fort_treasure']) {
      this.ui.showToast(`${personalization.clara.name}'s cozy tree fort 🌳 — so peaceful up here`, 'info');
      return;
    }
    store.update((s) => {
      s.flags['fort_treasure'] = true;
      s.money += 200;
    });
    store.addItem('gem', 1);
    playSfx(this, AUDIO.coin);
    this.ui.showToast(`You found ${personalization.clara.name}'s secret tree fort! 💛 +200g & a gem`, 'good');
  }

  private forage(idx: number): void {
    if (store.get().flags['forage' + idx]) {
      this.ui.showToast('Already foraged here today', 'info');
      return;
    }
    if (!store.trySpendEnergy('forage')) {
      this.ui.showToast('Too tired — rest up', 'warn');
      return;
    }
    this.actionBob();
    const spot = FORAGE[idx]!;
    store.update((s) => (s.flags['forage' + idx] = true));
    store.addItem(spot.item, 1);
    playSfx(this, AUDIO.harvest);
    this.spotSprites.get(idx)?.setAlpha(0.3);
    this.ui.showToast(`+1 ${getItem(spot.item)?.name ?? spot.item}`, 'good');
  }

  private routeTo(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, FW, FH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    if (Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y) <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, FW, FH, this.isBlocked);
    if (!adj) return;
    const path = findPath(this.playerTile, adj, FW, FH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    playSfx(this, AUDIO.pop);
    this.scene.stop();
    this.scene.resume('World');
  }

  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add.image(this.player.x, this.player.y - 16, tex).setOrigin(0.5, 0.5).setDepth(this.player.depth + 1);
    }
  }

  update(_time: number, delta: number): void {
    if (this.hat) {
      this.hat.setPosition(this.player.x, this.player.y - 16).setFlipX(this.player.flipX).setDepth(this.player.depth + 1);
    }
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.lastFacing = faceMove(this, this.player, dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.player.setDepth(fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        faceIdle(this, this.player, this.lastFacing);
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
      this.player.setDepth(this.player.y);
    }
  }
}
